import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const AdminAccountManagement = () => {
  const [activeTab, setActiveTab] = useState('deletion-requests');
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  // Recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [requestSearchTerm, setRequestSearchTerm] = useState('');
  
  // Pagination
  const [currentPageRequests, setCurrentPageRequests] = useState(1);
  const [currentPageUsers, setCurrentPageUsers] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'deletion-requests') {
        const res = await api.get('/admin/deletion-requests');
        setDeletionRequests(res.data.requests || []);
      } else if (activeTab === 'users') {
        const res = await api.get('/admin/users');
        setUsers(res.data.users || []);
      }
      // Charger les stats
      const statsRes = await api.get('/admin/account-stats');
      setStats(statsRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: '❌ Erreur lors du chargement des données' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleApprove = async (requestId) => {
    setActionLoading(requestId);
    try {
      await api.post(`/admin/deletion-requests/${requestId}/approve`);
      setMessage({ type: 'success', text: '✅ Compte supprimé avec succès (soft delete)' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || '❌ Erreur lors de l\'approbation' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setActionLoading(selectedRequest.id);
    try {
      await api.post(`/admin/deletion-requests/${selectedRequest.id}/reject`, { 
        commentaire: rejectReason 
      });
      setMessage({ type: 'success', text: '✅ Demande refusée' });
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: '❌ Erreur lors du refus' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendUser = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post(`/admin/users/${userId}/suspend`);
      setMessage({ type: 'success', text: '✅ Utilisateur suspendu' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || '❌ Erreur lors de la suspension' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivateUser = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post(`/admin/users/${userId}/reactivate`);
      setMessage({ type: 'success', text: '✅ Utilisateur réactivé' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: '❌ Erreur lors de la réactivation' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('⚠️ Voulez-vous vraiment supprimer ce compte ? Cette action appliquera un soft delete.')) return;
    setActionLoading(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      setMessage({ type: 'success', text: '✅ Utilisateur supprimé (soft delete)' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || '❌ Erreur lors de la suppression' });
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.statut === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Filtrer les demandes de suppression
  const filteredRequests = deletionRequests.filter(request => {
    const matchesSearch = request.user_nom?.toLowerCase().includes(requestSearchTerm.toLowerCase()) ||
                         request.user_email?.toLowerCase().includes(requestSearchTerm.toLowerCase());
    const matchesStatus = requestStatusFilter === 'all' || request.statut === requestStatusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination pour les demandes
  const totalPagesRequests = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPageRequests - 1) * itemsPerPage,
    currentPageRequests * itemsPerPage
  );

  // Pagination pour les utilisateurs
  const totalPagesUsers = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPageUsers - 1) * itemsPerPage,
    currentPageUsers * itemsPerPage
  );

  const pendingRequests = deletionRequests.filter(r => r.statut === 'en_attente');

  // Reset pagination quand les filtres changent
  useEffect(() => {
    setCurrentPageRequests(1);
  }, [requestSearchTerm, requestStatusFilter]);

  useEffect(() => {
    setCurrentPageUsers(1);
  }, [searchTerm, statusFilter]);

  const tabs = [
    { id: 'deletion-requests', label: 'Demandes de suppression', icon: '🗑️', badge: pendingRequests.length },
    { id: 'users', label: 'Gestion des utilisateurs', icon: '👥' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link 
              to="/admin"
              className="p-2 bg-white rounded-xl shadow-sm hover:shadow-md transition text-gray-500 hover:text-red-600"
            >
              ← Retour
            </Link>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                Gestion des Comptes
              </h1>
              <p className="text-gray-500 mt-1">Gérez les demandes de suppression et les utilisateurs</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <span className="text-xl">👥</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total_users || 0}</p>
                  <p className="text-xs text-gray-500">Total utilisateurs</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <span className="text-xl">✅</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.active_users || 0}</p>
                  <p className="text-xs text-gray-500">Actifs</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <span className="text-xl">⏸️</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.suspended_users || 0}</p>
                  <p className="text-xs text-gray-500">Suspendus</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <span className="text-xl">🗑️</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.deleted_users || 0}</p>
                  <p className="text-xs text-gray-500">Supprimés</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <span className="text-xl">📨</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{stats.pending_requests || 0}</p>
                  <p className="text-xs text-gray-500">En attente</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message Alert */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            <span className="text-xl">{message.type === 'success' ? '✅' : '❌'}</span>
            <span className="font-medium">{message.text}</span>
            <button 
              onClick={() => setMessage({ type: '', text: '' })}
              className="ml-auto text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="flex border-b border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-red-50 text-red-600 border-b-2 border-red-600'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.badge > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <span className="animate-spin inline-block text-4xl">⏳</span>
                <p className="text-gray-500 mt-4">Chargement...</p>
              </div>
            ) : (
              <>
                {/* Deletion Requests Tab */}
                {activeTab === 'deletion-requests' && (
                  <div>
                    {/* Barre de recherche et filtres */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="🔍 Rechercher par nom ou email..."
                          value={requestSearchTerm}
                          onChange={(e) => setRequestSearchTerm(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
                        />
                      </div>
                      <select
                        value={requestStatusFilter}
                        onChange={(e) => setRequestStatusFilter(e.target.value)}
                        className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
                      >
                        <option value="all">Tous les statuts</option>
                        <option value="en_attente">⏳ En attente</option>
                        <option value="approuve">✅ Approuvées</option>
                        <option value="refuse">❌ Refusées</option>
                      </select>
                    </div>

                    {/* Compteur résultats */}
                    <p className="text-sm text-gray-500 mb-4">
                      {filteredRequests.length} demande(s) trouvée(s)
                    </p>

                    {filteredRequests.length === 0 ? (
                      <div className="text-center py-12">
                        <span className="text-6xl">📭</span>
                        <p className="text-gray-500 mt-4">Aucune demande de suppression</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {paginatedRequests.map((request) => (
                          <div 
                            key={request.id}
                            className={`border rounded-2xl p-5 ${
                              request.statut === 'en_attente' ? 'border-amber-200 bg-amber-50/30' :
                              request.statut === 'approuve' ? 'border-green-200 bg-green-50/30' :
                              'border-red-200 bg-red-50/30'
                            }`}
                          >
                            <div className="flex flex-col md:flex-row md:items-start gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                                    {request.user_nom?.[0]?.toUpperCase() || '?'}
                                  </div>
                                  <div>
                                    <h3 className="font-bold text-gray-800">{request.user_nom}</h3>
                                    <p className="text-sm text-gray-500">{request.user_email}</p>
                                  </div>
                                  <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
                                    request.statut === 'en_attente' ? 'bg-amber-100 text-amber-700' :
                                    request.statut === 'approuve' ? 'bg-green-100 text-green-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {request.statut === 'en_attente' ? '⏳ En attente' :
                                     request.statut === 'approuve' ? '✅ Compte supprimé' : '✗ Refusée'}
                                  </span>
                                </div>
                                
                                <div className="bg-white rounded-xl p-4 border border-gray-200 mb-3">
                                  <p className="text-sm font-medium text-gray-600 mb-1">Raison de la demande :</p>
                                  <p className="text-gray-800">{request.raison}</p>
                                </div>
                                
                                <p className="text-xs text-gray-400">
                                  Demande du {new Date(request.date_demande).toLocaleDateString('fr-FR', {
                                    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                  })}
                                </p>
                                
                                {request.commentaire_admin && (
                                  <div className="mt-3 p-3 bg-gray-100 rounded-xl">
                                    <p className="text-sm font-medium text-gray-600">Commentaire admin :</p>
                                    <p className="text-gray-700">{request.commentaire_admin}</p>
                                  </div>
                                )}
                              </div>
                              
                              {request.statut === 'en_attente' && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleApprove(request.id)}
                                    disabled={actionLoading === request.id}
                                    className="px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition disabled:opacity-50"
                                  >
                                    {actionLoading === request.id ? '...' : '✓ Approuver'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setShowRejectModal(true);
                                    }}
                                    className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition"
                                  >
                                    ✗ Refuser
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pagination Demandes */}
                    {totalPagesRequests > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-200">
                        <button
                          onClick={() => setCurrentPageRequests(1)}
                          disabled={currentPageRequests === 1}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ««
                        </button>
                        <button
                          onClick={() => setCurrentPageRequests(p => Math.max(1, p - 1))}
                          disabled={currentPageRequests === 1}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          «
                        </button>
                        <span className="px-4 py-2 text-sm text-gray-600">
                          Page {currentPageRequests} / {totalPagesRequests}
                        </span>
                        <button
                          onClick={() => setCurrentPageRequests(p => Math.min(totalPagesRequests, p + 1))}
                          disabled={currentPageRequests === totalPagesRequests}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          »
                        </button>
                        <button
                          onClick={() => setCurrentPageRequests(totalPagesRequests)}
                          disabled={currentPageRequests === totalPagesRequests}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          »»
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div>
                    {/* Filters */}
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="🔍 Rechercher par nom ou email..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
                        />
                      </div>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
                      >
                        <option value="all">Tous les statuts</option>
                        <option value="actif">✅ Actifs</option>
                        <option value="suspendu">⏸️ Suspendus</option>
                        <option value="supprime">🗑️ Supprimés</option>
                      </select>
                    </div>

                    {/* Users List */}
                    {/* Compteur résultats */}
                    <p className="text-sm text-gray-500 mb-4">
                      {filteredUsers.length} utilisateur(s) trouvé(s)
                    </p>

                    {filteredUsers.length === 0 ? (
                      <div className="text-center py-12">
                        <span className="text-6xl">👤</span>
                        <p className="text-gray-500 mt-4">Aucun utilisateur trouvé</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Utilisateur</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Rôle</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Inscription</th>
                              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedUsers.map((user) => (
                              <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-4 px-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                                      {user.nom?.[0]?.toUpperCase() || '?'}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-800">{user.nom}</p>
                                      <p className="text-sm text-gray-500">{user.email}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-4 px-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    user.role === 'admin' ? 'bg-red-100 text-red-600' :
                                    user.role === 'author' ? 'bg-green-100 text-green-600' :
                                    'bg-blue-100 text-blue-600'
                                  }`}>
                                    {user.role === 'admin' ? '👑 Admin' :
                                     user.role === 'author' ? '✨ Auteur' : '👤 User'}
                                  </span>
                                </td>
                                <td className="py-4 px-4">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    user.statut === 'actif' ? 'bg-green-100 text-green-600' :
                                    user.statut === 'suspendu' ? 'bg-amber-100 text-amber-600' :
                                    'bg-red-100 text-red-600'
                                  }`}>
                                    {user.statut === 'actif' ? '✅ Actif' :
                                     user.statut === 'suspendu' ? '⏸️ Suspendu' : '🗑️ Supprimé'}
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-sm text-gray-500">
                                  {user.date_inscription ? new Date(user.date_inscription).toLocaleDateString('fr-FR') : '-'}
                                </td>
                                <td className="py-4 px-4">
                                  <div className="flex justify-end gap-2">
                                    {user.role !== 'admin' && (
                                      <>
                                        {user.statut === 'actif' && (
                                          <button
                                            onClick={() => handleSuspendUser(user.id)}
                                            disabled={actionLoading === user.id}
                                            className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-lg hover:bg-amber-200 transition disabled:opacity-50"
                                            title="Suspendre"
                                          >
                                            ⏸️
                                          </button>
                                        )}
                                        {user.statut === 'suspendu' && (
                                          <button
                                            onClick={() => handleReactivateUser(user.id)}
                                            disabled={actionLoading === user.id}
                                            className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-lg hover:bg-green-200 transition disabled:opacity-50"
                                            title="Réactiver"
                                          >
                                            ✅
                                          </button>
                                        )}
                                        {user.statut !== 'supprime' && (
                                          <button
                                            onClick={() => handleDeleteUser(user.id)}
                                            disabled={actionLoading === user.id}
                                            className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition disabled:opacity-50"
                                            title="Supprimer"
                                          >
                                            🗑️
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Utilisateurs */}
                    {totalPagesUsers > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-6 pt-6 border-t border-gray-200">
                        <button
                          onClick={() => setCurrentPageUsers(1)}
                          disabled={currentPageUsers === 1}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ««
                        </button>
                        <button
                          onClick={() => setCurrentPageUsers(p => Math.max(1, p - 1))}
                          disabled={currentPageUsers === 1}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          «
                        </button>
                        <span className="px-4 py-2 text-sm text-gray-600">
                          Page {currentPageUsers} / {totalPagesUsers}
                        </span>
                        <button
                          onClick={() => setCurrentPageUsers(p => Math.min(totalPagesUsers, p + 1))}
                          disabled={currentPageUsers === totalPagesUsers}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          »
                        </button>
                        <button
                          onClick={() => setCurrentPageUsers(totalPagesUsers)}
                          disabled={currentPageUsers === totalPagesUsers}
                          className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          »»
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">✗</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Refuser la demande</h3>
              <p className="text-gray-600 text-sm">
                Vous pouvez ajouter un commentaire pour expliquer le refus à l'utilisateur.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Commentaire (optionnel)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Votre compte a des projets en cours..."
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-400 resize-none"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedRequest(null);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading === selectedRequest?.id}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition disabled:opacity-50"
              >
                {actionLoading === selectedRequest?.id ? 'Refus...' : 'Confirmer le refus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccountManagement;
